"use client";

import { useState } from "react";
import Link from "next/link";
import { Menu } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from "@/components/ui/sheet";
import type { ResolvedBrand } from "@/config/landing";
import { openCrispChat } from "@/lib/crisp";
import { Logo } from "./logo";

function contactHref(brand: ResolvedBrand): string {
  return `mailto:${brand.supportEmail}?subject=${encodeURIComponent(`${brand.name} — pricing inquiry`)}`;
}

export function Navbar({ brand }: { brand: ResolvedBrand }) {
  const { user, loading } = useAuth();
  const [open, setOpen] = useState(false);

  const navItems = (
    <>
      <a
        href="#features"
        className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        Features
      </a>
      <a
        href="#faq"
        className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        FAQ
      </a>
      {!loading && (
        <>
          {user ? (
            <Button render={<Link href="/dashboard" />} size="sm">
              Dashboard
            </Button>
          ) : (
            <>
              <Button render={<Link href="/login" />} variant="ghost" size="sm">
                Login
              </Button>
              <Button
                render={<a href={contactHref(brand)} />}
                size="sm"
                onClick={(e) => {
                  if (process.env.NEXT_PUBLIC_CRISP_WEBSITE_ID) {
                    e.preventDefault();
                    openCrispChat();
                  }
                }}
              >
                Contact us
              </Button>
            </>
          )}
        </>
      )}
    </>
  );

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/80 backdrop-blur">
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        <Link href="/" className="flex items-center gap-2 font-sans text-xl font-light tracking-tight">
          {brand.logoUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={brand.logoUrl}
              alt={`${brand.name} logo`}
              className="h-6 w-auto max-w-[120px] object-contain"
            />
          ) : (
            <Logo size={24} idSuffix="-nav" />
          )}
          <span className="text-primary">{brand.name}</span>
        </Link>

        <nav className="hidden items-center gap-4 md:flex">
          <ThemeToggle />
          {navItems}
        </nav>

        <div className="flex items-center gap-2 md:hidden">
          <ThemeToggle />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side="right">
            <SheetHeader>
              <SheetTitle>Menu</SheetTitle>
            </SheetHeader>
            <nav className="flex flex-col gap-4 p-4">
              <SheetClose
                render={<a href="#features" />}
                className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                Features
              </SheetClose>
              <SheetClose
                render={<a href="#faq" />}
                className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                FAQ
              </SheetClose>
              {!loading && (
                <>
                  {user ? (
                    <SheetClose render={<span />}>
                      <Button
                        render={<Link href="/dashboard" />}
                        className="w-full"
                        size="sm"
                      >
                        Dashboard
                      </Button>
                    </SheetClose>
                  ) : (
                    <>
                      <SheetClose render={<span />}>
                        <Button
                          render={<Link href="/login" />}
                          variant="ghost"
                          className="w-full"
                          size="sm"
                        >
                          Login
                        </Button>
                      </SheetClose>
                      <SheetClose render={<span />}>
                        <Button
                          render={<a href={contactHref(brand)} />}
                          className="w-full"
                          size="sm"
                          onClick={(e) => {
                            if (process.env.NEXT_PUBLIC_CRISP_WEBSITE_ID) {
                              e.preventDefault();
                              openCrispChat();
                              setOpen(false);
                            }
                          }}
                        >
                          Contact us
                        </Button>
                      </SheetClose>
                    </>
                  )}
                </>
              )}
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
